import { useCallback, useEffect, useState } from 'react';
import { getUsageBalance } from '../api/usage';
import { PotType, UsageBalanceResponse } from '../api/usage/types';

export const useUsageBalance = () => {
  const [balance, setBalance] = useState<UsageBalanceResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getUsageBalance();
      setBalance(data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err : new Error('Failed to load usage balance'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load().catch((err: unknown) =>
      setError(err instanceof Error ? err : new Error('Unknown error')),
    );
  }, [load]);

  // Calculate total credits excluding FREE and PAYG (on-demand) pots
  const totalCreditsRemaining =
    balance?.pots
      .filter((pot) => pot.type !== PotType.FREE && pot.type !== PotType.PAYG)
      .reduce((sum, pot) => sum + pot.remaining, 0) ?? 0;

  return {
    balance,
    totalCreditsRemaining,
    isLoading,
    error,
    refresh: load,
  };
};
